import { Formik, Form } from 'formik';
import { SchemaOf, object, string } from 'yup';
import { useState } from 'react';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { FormSection } from '@@/form-components/FormSection';
import { TextTip } from '@@/Tip/TextTip';
import { FormActions } from '@@/form-components/FormActions';

import { RegistryTestConnection } from '../TestConnection/RegistryTestConnection';

export interface RegistryFormDockerhubValues {
  Name: string;
  Username: string;
  Password: string;
}

interface Props {
  initialValues: RegistryFormDockerhubValues;
  onSubmit: (values: RegistryFormDockerhubValues) => void | Promise<void>;
  submitLabel: string;
  isLoading: boolean;
  nameIsUsed: (name: string) => Promise<boolean>;
}

export function RegistryFormDockerhub({
  initialValues,
  onSubmit,
  submitLabel,
  isLoading,
  nameIsUsed,
}: Props) {
  const [isConnectionTested, setIsConnectionTested] = useState(false);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={() => validationSchema(nameIsUsed)}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
    >
      {({ errors, isValid, values, setFieldValue }) => (
        <Form className="form-horizontal">
          <FormSection title="重要提示">
            <TextTip color="blue">
              <p>
                有关如何生成 DockerHub 访问令牌 (Access Token) 的信息，请参阅{' '}
                <a
                  href="https://docs.docker.com/security/access-tokens/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  DockerHub 官方指引
                </a>
                。
              </p>
            </TextTip>
          </FormSection>

          <FormSection title="DockerHub 账户信息">
            <FormControl
              label="仓库名称"
              inputId="registry_name"
              errors={errors.Name}
              required
            >
              <Input
                id="registry_name"
                name="Name"
                placeholder="例如 dockerhub-prod-us"
                data-cy="component-registryName"
                value={values.Name}
                onChange={(event) => {
                  setIsConnectionTested(false);
                  setFieldValue('Name', event.target.value);
                }}
              />
            </FormControl>

            <FormControl
              label="DockerHub 用户名"
              inputId="registry_username"
              errors={errors.Username}
              required
            >
              <Input
                id="registry_username"
                name="Username"
                data-cy="component-registryUsername"
                value={values.Username}
                onChange={(event) => {
                  setIsConnectionTested(false);
                  setFieldValue('Username', event.target.value);
                }}
              />
            </FormControl>

            <FormControl
              label="DockerHub 访问令牌 (Access Token)"
              inputId="registry_password"
              errors={errors.Password}
              required
            >
              <Input
                id="registry_password"
                name="Password"
                type="password"
                data-cy="component-registryPassword"
                value={values.Password}
                onChange={(event) => {
                  setIsConnectionTested(false);
                  setFieldValue('Password', event.target.value);
                }}
              />
            </FormControl>
          </FormSection>

          <RegistryTestConnection
            values={values}
            onTestSuccess={() => setIsConnectionTested(true)}
            disabled={!isValid}
            isConnectionTested={isConnectionTested}
          />

          <FormActions
            isLoading={isLoading}
            isValid={isValid && isConnectionTested}
            submitLabel={submitLabel}
            errors={errors}
            loadingText="正在处理..."
            data-cy="registry-form-submit"
          />
        </Form>
      )}
    </Formik>
  );

  function handleSubmit(values: RegistryFormDockerhubValues) {
    return onSubmit(values);
  }
}

function validationSchema(
  nameIsUsed: (name: string) => Promise<boolean>
): SchemaOf<RegistryFormDockerhubValues> {
  return object({
    Name: string()
      .required('此字段为必填项。')
      .test(
        'name-not-used',
        '已存在同名的镜像仓库。',
        async (name) => !(await nameIsUsed(name || ''))
      ),
    Username: string().required('此字段为必填项。'),
    Password: string().required('此字段为必填项。'),
  });
}
